// src/pages/InventoryManagementPage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Button, Typography, Modal, TextField,
    Select, MenuItem, InputLabel, FormControl, CircularProgress, Alert, Paper
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {  esES } from '@mui/x-data-grid/locales';
import api from '../api/index.js';
import InventoryImportModal from './InventoryImportModal.js';
import { printComponent } from '../utils/printComponent.js';
import PriceLabel from '../components/printables/PriceLabel.js';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 500 },
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const InventoryManagementPage = () => {
    // --- ESTADOS ---
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState('');
    const [inventory, setInventory] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingInventory, setLoadingInventory] = useState(false);
    const [error, setError] = useState('');
    
    const [openAddEditModal, setOpenAddEditModal] = useState(false);
    const [openImportModal, setOpenImportModal] = useState(false);

    const [formData, setFormData] = useState({});
    const [editingItem, setEditingItem] = useState(null);

const handlePrintLabel = (product) => {
        // Creamos una instancia del componente que queremos imprimir
        const componentToPrint = <PriceLabel product={product} />;
        // Se la pasamos a nuestra función de utilidad
        printComponent(componentToPrint);
    };


    // --- CARGA DE DATOS ---
    const fetchInventoryForStore = useCallback(async (storeId) => {
        if (!storeId) {
            setInventory([]);
            return;
        }
        setLoadingInventory(true);
        setError('');
        try {
            const storeDetailsRes = await api.get(`/stores/${storeId}`);
            const selectedStoreData = storeDetailsRes.data;
            const inventoryStoreId = selectedStoreData.inventory_source_store_id || storeId;

            const response = await api.get(`/inventory/store/${inventoryStoreId}?timestamp=${new Date().getTime()}`);
            
            const processedData = response.data.map(item => ({
                ...item,
                sale_price: parseFloat(item.sale_price) || 0,
                quantity: parseFloat(item.quantity) || 0,
                purchase_price: parseFloat(item.purchase_price) || 0
            }));
            setInventory(processedData);
        } catch (error) {
            console.error("Error al cargar inventario:", error);
            setError("No se pudo cargar el inventario para esta tienda.");
            setInventory([]);
        } finally {
            setLoadingInventory(false);
        }
    }, []);

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                const [storesRes, productsRes] = await Promise.all([
                    api.get('/stores'),
                    api.get('/products')
                ]);
                
                const storesData = storesRes.data;
                setStores(storesData);
                setAllProducts(productsRes.data);

                if (storesData.length > 0) {
                    const firstStoreId = storesData[0].id;
                    setSelectedStore(firstStoreId);
                    await fetchInventoryForStore(firstStoreId);
                }
            } catch (error) {
                console.error("Error al cargar datos iniciales:", error);
                setError("No se pudieron cargar los datos de la página.");
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, [fetchInventoryForStore]);

    // --- MANEJADORES ---
    const handleStoreChange = (event) => {
        const storeId = event.target.value;
        setSelectedStore(storeId);
        fetchInventoryForStore(storeId);
    };

    const handleOpenAddModal = () => {
        setEditingItem(null);
        setFormData({ productId: '', quantity: 0, sale_price: 0 });
        setError('');
        setOpenAddEditModal(true);
    };

    const handleOpenEditModal = (item) => {
        setEditingItem(item);
        setFormData({
            productId: item.product_id,
            quantity: item.quantity,
            sale_price: item.sale_price,
        });
        setError('');
        setOpenAddEditModal(true);
    };

    const handleCloseModal = () => setOpenAddEditModal(false);

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        const productId = editingItem ? editingItem.product_id : formData.productId;
        if (!productId) {
            setError('Debes seleccionar un producto.');
            return;
        }
        
        const storeToUpdate = stores.find(s => s.id === selectedStore)?.inventory_source_store_id || selectedStore;
        
        try {
            const payload = {
                quantity: parseFloat(formData.quantity),
                sale_price: parseFloat(formData.sale_price)
            };
            await api.post(`/inventory/store/${storeToUpdate}/product/${productId}`, payload);
            handleCloseModal();
            await fetchInventoryForStore(selectedStore);
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar.');
        }
    };

    // --- COLUMNAS Y RENDERIZADO ---
    const columns = [
        { field: 'sku', headerName: 'SKU', width: 150 },
        { field: 'name', headerName: 'Nombre', width: 300 },
        { field: 'quantity', headerName: 'Stock', width: 100, type: 'number' },
        { 
            field: 'purchase_price', headerName: 'Costo', width: 120, type: 'number',
            renderCell: (params) => {
                const value = params.value;
                if (value == null || isNaN(Number(value))) return '';
                return `$${Number(value).toFixed(2)}`;
            }
        },
        { 
            field: 'sale_price', headerName: 'Precio Venta', width: 150, type: 'number',
            renderCell: (params) => {
                const value = params.value;
                if (value == null || isNaN(Number(value))) return '';
                return `$${Number(value).toFixed(2)}`;
            }
        },
        {
            field: 'actions', 
            headerName: 'Acciones', 
            width: 300, // Aumentamos el ancho
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" size="small" onClick={() => handleOpenEditModal(params.row)}>
                        Editar Stock/Precio
                    </Button>
                    <Button variant="outlined" size="small" color="secondary" onClick={() => handlePrintLabel(params.row)}>
                        Imprimir Etiqueta
                    </Button>
                </Box>
            ),
        },
    ];

    const selectedStoreDetails = stores.find(s => s.id === selectedStore);

    if (loading) return <CircularProgress />;

    return (
        <Box>
            <Typography variant="h5" sx={{ mb: 2 }}>Gestión de Inventario</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Tienda Seleccionada</InputLabel>
                <Select value={selectedStore} label="Tienda Seleccionada" onChange={handleStoreChange}>
                    {stores.map(store => <MenuItem key={store.id} value={store.id}>{store.name}</MenuItem>)}
                </Select>
            </FormControl>

            {selectedStore && (
                <Box sx={{ height: '70vh', width: '100%' }}>
                    {selectedStoreDetails?.inventory_source_store_id && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Esta es una tienda secundaria. El inventario mostrado y gestionado corresponde a la tienda principal: 
                            <strong> {stores.find(s => s.id === selectedStoreDetails.inventory_source_store_id)?.name}</strong>.
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Button variant="contained" onClick={handleOpenAddModal}>Añadir / Editar Producto</Button>
                        <Button variant="outlined" onClick={() => setOpenImportModal(true)}>Importar Inventario</Button>
                    </Box>
                    <DataGrid
                        rows={inventory}
                        getRowId={(row) => row.product_id}
                        columns={columns}
                        loading={loadingInventory}
                        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                    />
                </Box>
            )}

            <Modal open={openAddEditModal} onClose={handleCloseModal}>
                <Box sx={style}>
                    <Typography variant="h6">
                        {editingItem ? `Editando: ${editingItem.name}` : 'Añadir Producto al Inventario'}
                    </Typography>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Producto</InputLabel>
                        <Select
                            name="productId"
                            value={formData.productId || ''}
                            label="Producto"
                            onChange={handleFormChange}
                            disabled={!!editingItem}
                        >
                            {allProducts.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField margin="normal" required fullWidth label="Cantidad (Stock)" name="quantity" type="number" value={formData.quantity || ''} onChange={handleFormChange} />
                    <TextField margin="normal" required fullWidth label="Precio de Venta" name="sale_price" type="number" value={formData.sale_price || ''} onChange={handleFormChange} />
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleCloseModal} sx={{ mr: 1 }}>Cancelar</Button>
                        <Button variant="contained" onClick={handleSubmit}>Guardar</Button>
                    </Box>
                </Box>
            </Modal>

            <InventoryImportModal
                open={openImportModal}
                onClose={() => setOpenImportModal(false)}
                storeId={selectedStore}
                onImportSuccess={() => fetchInventoryForStore(selectedStore)}
            />
        </Box>
    );
};

export default InventoryManagementPage;